import { treaty } from "@elysiajs/eden";
import type { App } from "../../index.ts";

/**
 * Typed Eden client. The backend's `App` type is exported from src/index.ts.
 * In the browser, requests go to the same origin (Elysia serves both /api and /).
 * Cookies are sent automatically; treaty uses fetch under the hood with
 * `credentials: "include"` is the default for same-origin requests.
 */
export const api = treaty<App>(
	typeof window !== "undefined" ? window.location.origin : "",
	{
		fetch: { credentials: "include" },
	},
);

function extractErrorMessage(value: unknown, status: unknown): string {
	if (value && typeof value === "object") {
		const v = value as { error?: { message?: string }; message?: string };
		if (v.error?.message) return v.error.message;
		if (v.message) return v.message;
	}
	if (typeof value === "string" && value.length > 0) {
		return value;
	}
	return `HTTP ${typeof status === "number" ? status : "error"}`;
}

/**
 * Unwraps an Eden treaty response. Throws a friendly error when the server
 * returned an error envelope; otherwise returns the parsed JSON body so
 * callers can read fields like `.users`, `.cluster`, `.operationId` directly.
 *
 * The type signature is intentionally loose — Eden's TreatyResponse uses
 * `unknown` for `error.status` because the route's error union isn't tracked
 * at compile time. Callers narrow the returned shape with a local cast
 * (`as Promise<MyResponse>`) where useful.
 *
 * Use with TanStack Query mutation/queryFn:
 *   const data = await unwrap(api.api.cluster.get());
 */
export async function unwrap<T>(
	p: Promise<{
		data: T | null;
		error: { status: unknown; value: unknown } | null;
	}>,
): Promise<T> {
	const { data, error } = await p;
	if (error) {
		throw new Error(extractErrorMessage(error.value, error.status));
	}
	if (data === null) {
		throw new Error("Empty response body");
	}
	return data;
}
